import React, { useState, useEffect } from 'react';
import { Combobox, Form } from '@bynder/design-system';
import { IconUser } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { API } from './api';
import { Errors, User } from './types';
import { UserResult, UserSelected } from './UserResult';

interface Props {
	selectedUser?: User;
	onSelectUser: (item: User) => void;
	onRemoveUser: () => void;
	isDisabled?: boolean;
	errorName?: Errors;
}

const SelectUser: React.FC<Props> = ({ selectedUser, onSelectUser, onRemoveUser, errorName }) => {
	const [results, setResults] = useState<User[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const { translate } = useTranslate();

	useEffect(() => {
		if (searchTerm.trim().length > 0) {
			const searchResults = async () => {
				try {
					const resultUsers = await API.searchIdentity(searchTerm);

					setResults(resultUsers);
				} catch (err) {
					throw new Error(`error calling endpoint: ${err}`);
				}
			};
			searchResults();
		} else {
			setResults([]);
		}
	}, [searchTerm]);

	return selectedUser ? (
		<>
			<UserSelected {...selectedUser} email={selectedUser.email} onClear={onRemoveUser} />
			{errorName === 'backup_unavailable' && (
				<Form.HelperText icon={<Form.IconError />} isInvalid>
					{translate('user-outofoffice.BackupUnavailable')}
				</Form.HelperText>
			)}
		</>
	) : (
		<Combobox
			openOn="type"
			placeholder={translate('user-outofoffice.SearchForAUser')}
			value={searchTerm}
			onChange={(term) => setSearchTerm(term.trim())}
			isAnyValueAllowed
			icon={<IconUser />}
		>
			{results.length > 0 ? (
				results.map((result) => (
					<Combobox.ListItem
						onClick={() => {
							onSelectUser({
								id: result.id,
								name: result.name,
								email: result.email,
							});
							setSearchTerm('');
						}}
						key={result.id}
					>
						<UserResult {...result} />
					</Combobox.ListItem>
				))
			) : (
				<Combobox.Text>{translate('noresults')}</Combobox.Text>
			)}
		</Combobox>
	);
};

export default SelectUser;
